import { useLexicalComposerContext } from '@fluentui-copilot/react-text-editor';
import * as React from 'react';
import { $createChatInputEntityNode, $isChatInputEntityNode, ChatInputEntityNode } from './ChatInputEntity.node';
import type { ChatInputEntityPluginRef } from './ChatInputEntityPlugin.types';
import type { ChatInputEntityPluginProps } from '@fluentui-copilot/chat-input-plugins';
import { ChatInputEntityPluginBase } from '@fluentui-copilot/chat-input-plugins';
import type { ChatInputEntityProps } from '../../ChatInputEntity';

export const ChatInputEntityPlugin = <ExtraDataType,>(
  props: ChatInputEntityPluginProps<ExtraDataType, ChatInputEntityProps> & {
    controlRef: React.Ref<ChatInputEntityPluginRef<ExtraDataType>>;
  },
): null => {
  const { id, controlRef, onChatInputEntityAdded, onChatInputEntityDeleted, skipInitialization } = props;
  const [editor] = useLexicalComposerContext();

  const chatInputEntityPluginBase = React.useRef<ChatInputEntityPluginBase<
    ExtraDataType,
    ChatInputEntityProps,
    ChatInputEntityNode<ExtraDataType>
  > | null>(null);

  React.useEffect(() => {
    chatInputEntityPluginBase.current = new ChatInputEntityPluginBase<
      ExtraDataType,
      ChatInputEntityProps,
      ChatInputEntityNode<ExtraDataType>
    >(
      editor,
      id,
      ChatInputEntityNode,
      $createChatInputEntityNode,
      $isChatInputEntityNode,
      onChatInputEntityAdded,
      onChatInputEntityDeleted,
    );

    return () => chatInputEntityPluginBase.current?.cleanup();
  }, [editor, id, onChatInputEntityAdded, onChatInputEntityDeleted, skipInitialization]);

  React.useImperativeHandle(
    controlRef,
    () => ({
      insertChatInputEntity(props) {
        return chatInputEntityPluginBase.current?.insertChatInputEntity(props);
      },

      getActiveEntities() {
        return chatInputEntityPluginBase.current?.getActiveEntities() ?? [];
      },

      removeChatInputEntity(keyOrPredicate) {
        chatInputEntityPluginBase.current?.removeChatInputEntity(keyOrPredicate);
      },

      updateChatInputEntityProps(keyOrPredicate, props) {
        chatInputEntityPluginBase.current?.updateChatInputEntityProps(keyOrPredicate, props);
      },
    }),
    [],
  );
  return null;
};
